/**
 * UrgentDeadlinesAlert Component
 *
 * Prominent alert banner for dashboard showing deadlines within 14 days.
 * Displayed between WelcomeSection and AdvocacySuite.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import {
  DeadlineSummary,
  PRIORITY_STYLES,
  getDaysUntil,
  formatDaysRemaining,
} from './types';
import { AlertTriangle, Clock, ChevronRight, Loader2 } from 'lucide-react';

interface GetUpcomingDeadlinesResponse {
  success: boolean;
  deadlines: Array<DeadlineSummary & { caseFileName?: string }>;
  totalCount: number;
  error?: string;
}

interface UrgentDeadlinesAlertProps {
  activeCaseFileId?: string;
  daysAhead?: number;
}

export function UrgentDeadlinesAlert({
  activeCaseFileId,
  daysAhead = 14,
}: UrgentDeadlinesAlertProps): JSX.Element | null {
  const navigate = useNavigate();
  const [deadlines, setDeadlines] = useState<Array<DeadlineSummary & { caseFileName?: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUrgent = async () => {
      setLoading(true);

      try {
        const getUpcomingDeadlines = httpsCallable<
          { caseFileId?: string; daysAhead?: number; limit?: number },
          GetUpcomingDeadlinesResponse
        >(functions, 'getUpcomingDeadlines');

        const result = await getUpcomingDeadlines({
          caseFileId: activeCaseFileId,
          daysAhead,
          limit: 10,
        });

        if (result.data.success) {
          // Filter to only show overdue and due_soon, plus upcoming within daysAhead
          const urgent = result.data.deadlines.filter((d) => {
            const days = getDaysUntil(d.dueDate);
            return days <= daysAhead;
          });
          setDeadlines(urgent);
        }
      } catch (err) {
        console.error('Error loading urgent deadlines:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUrgent();
  }, [activeCaseFileId, daysAhead]);

  const handleDeadlineClick = (deadline: DeadlineSummary) => {
    navigate(`/casefile/${deadline.caseFileId}/deadlines`);
  };

  const handleViewAll = () => {
    if (activeCaseFileId) {
      navigate(`/casefile/${activeCaseFileId}/deadlines`);
    }
  };

  // Don't render if loading or no urgent deadlines
  if (loading) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
        <Loader2 size={20} className="animate-spin text-amber-600" />
        <span className="text-amber-800 text-sm">Checking deadlines...</span>
      </div>
    );
  }

  if (deadlines.length === 0) {
    return null;
  }

  // Separate overdue from upcoming
  const overdue = deadlines.filter((d) => d.status === 'overdue');
  const dueSoon = deadlines.filter((d) => d.status === 'due_soon');
  const upcoming = deadlines.filter((d) => d.status === 'upcoming');

  // Determine alert severity
  const hasOverdue = overdue.length > 0;
  const hasDueSoon = dueSoon.length > 0;

  const alertStyle = hasOverdue
    ? 'bg-red-50 border-red-200'
    : hasDueSoon
      ? 'bg-amber-50 border-amber-200'
      : 'bg-sky-50 border-sky-200';

  const iconColor = hasOverdue
    ? 'text-red-600'
    : hasDueSoon
      ? 'text-amber-600'
      : 'text-sky-600';

  const headerColor = hasOverdue
    ? 'text-red-800'
    : hasDueSoon
      ? 'text-amber-800'
      : 'text-sky-800';

  return (
    <div className={`${alertStyle} border rounded-xl p-4 lg:p-5`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {hasOverdue ? (
            <AlertTriangle size={20} className={iconColor} />
          ) : (
            <Clock size={20} className={iconColor} />
          )}
          <h3 className={`font-semibold ${headerColor}`}>
            {hasOverdue
              ? `${overdue.length} Overdue Deadline${overdue.length > 1 ? 's' : ''}`
              : hasDueSoon
                ? `${dueSoon.length} Deadline${dueSoon.length > 1 ? 's' : ''} Due Soon`
                : `${upcoming.length} Upcoming Deadline${upcoming.length > 1 ? 's' : ''}`}
          </h3>
        </div>
        {activeCaseFileId && (
          <button
            onClick={handleViewAll}
            className="text-sm font-medium text-evergreen hover:text-deep-forest flex items-center gap-1"
          >
            View Watchtower
            <ChevronRight size={16} />
          </button>
        )}
      </div>

      {/* Deadline Items */}
      <div className="space-y-2">
        {/* Show overdue first, then due soon, then upcoming - max 5 total */}
        {[...overdue, ...dueSoon, ...upcoming].slice(0, 5).map((deadline) => {
          const priorityStyle = PRIORITY_STYLES[deadline.priority];
          const daysRemaining = formatDaysRemaining(deadline.dueDate);
          const isOverdue = deadline.status === 'overdue';
          const isDueSoon = deadline.status === 'due_soon';

          return (
            <button
              key={deadline.deadlineId}
              onClick={() => handleDeadlineClick(deadline)}
              className={`w-full text-left p-3 rounded-lg bg-white/80 hover:bg-white border-l-4 ${priorityStyle.border} transition-all hover:shadow-sm`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-ink truncate">{deadline.title}</p>
                  {deadline.caseFileName && (
                    <p className="text-xs text-muted truncate">{deadline.caseFileName}</p>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <span
                    className={`text-sm font-semibold ${
                      isOverdue
                        ? 'text-red-600'
                        : isDueSoon
                          ? 'text-amber-600'
                          : 'text-sky-600'
                    }`}
                  >
                    {daysRemaining}
                  </span>
                </div>
              </div>
            </button>
          );
        })}

        {/* More indicator */}
        {deadlines.length > 5 && (
          <p className="text-center text-sm text-muted pt-1">
            +{deadlines.length - 5} more deadline{deadlines.length - 5 > 1 ? 's' : ''}
          </p>
        )}
      </div>
    </div>
  );
}

export default UrgentDeadlinesAlert;
